import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Save, PenTool, CheckSquare, Circle,
  Type, Calendar, Trash2, Plus, MousePointer, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import RecipientRoleSelect from '../components/RecipientRoleSelect';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

const FIELD_TYPES = {
  SIGNATURE: 'signature',
  INITIALS: 'initials',
  DATE: 'date',
  TEXT: 'text',
  EMAIL: 'email',
  NAME: 'name',
  COMPANY: 'company',
  TITLE: 'title',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  DROPDOWN: 'dropdown',
  ATTACHMENT: 'attachment',
  FORMULA: 'formula',
  NOTE: 'note',
  APPROVE: 'approve',
  DECLINE: 'decline'
};

const TemplateFieldEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, accessToken } = useContext(AuthContext);
  
  const [template, setTemplate] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [fields, setFields] = useState([]);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pageWidth, setPageWidth] = useState(800); // Use width instead of scale for consistent rendering
  const scale = 1; // Using 1:1 pixel mapping with pageWidth

  // Template recipients management (only 1 Signer allowed, multiple CC)
  const [recipients, setRecipients] = useState([
    { name: 'Signer 1', email: '', role: 'Signer' }
  ]);

  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // For dragging individual radio options within a group
  const [draggingRadioOption, setDraggingRadioOption] = useState(null); // { fieldId, optionIndex }
  const [radioOptionOffset, setRadioOptionOffset] = useState({ x: 0, y: 0 });

  // Memoize PDF file configuration to prevent unnecessary re-renders
  const pdfFile = useMemo(() => {
    if (!pdfUrl) return null;
    return {
      url: pdfUrl,
      httpHeaders: { Authorization: `Bearer ${accessToken}` }
    };
  }, [pdfUrl, accessToken]);

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  // Calculate PDF width based on container (matching SigningSession.js)
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - 32; // Account for padding
        setPageWidth(Math.min(width, 800)); // Max 800px
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const fetchTemplate = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/templates/${id}`, { headers });
      setTemplate(response.data);
      
      // Fetch template versions to get the document
      const versionsResponse = await axios.get(`${API}/templates/${id}/versions`, { headers });
      console.log('[DEBUG] Versions response:', versionsResponse.data);
      if (versionsResponse.data && versionsResponse.data.length > 0) {
        const latestVersion = versionsResponse.data[0];
        console.log('[DEBUG] Latest version:', latestVersion);
        console.log('[DEBUG] Schema type:', typeof latestVersion.schema);
        console.log('[DEBUG] Schema value:', latestVersion.schema);
        if (latestVersion.doc_file_id) {
          // Fetch file URL
          setPdfUrl(`${API}/files/${latestVersion.doc_file_id}`);
          
          // Load existing fields from schema
          // Handle both parsed object and JSON string (defensive programming)
          let schema = latestVersion.schema;
          if (typeof schema === 'string') {
            console.log('[DEBUG] Schema is string, parsing...');
            try {
              schema = JSON.parse(schema);
            } catch (e) {
              console.error('[DEBUG] Failed to parse schema:', e);
              schema = null;
            }
          }
          
          if (schema && schema.fields) {
            console.log('[DEBUG] Setting fields:', schema.fields);
            setFields(schema.fields);
          } else {
            console.log('[DEBUG] Schema or fields is missing:', {
              hasSchema: !!schema,
              schemaType: typeof schema,
              hasFields: schema && schema.fields
            });
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load template');
      navigate('/templates');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleAddField = (e) => {
    if (!selectedFieldType) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Determine field dimensions based on type - minimal sizing to fit input control only
    let width, height;
    if (selectedFieldType === FIELD_TYPES.SIGNATURE) {
      width = 150; height = 40;  // Tight signature box
    } else if (selectedFieldType === FIELD_TYPES.INITIALS) {
      width = 50; height = 40;   // Tight initials box
    } else if (selectedFieldType === FIELD_TYPES.CHECKBOX) {
      width = 20; height = 20;   // Checkbox size
    } else if (selectedFieldType === FIELD_TYPES.RADIO) {
      width = 24; height = 48;   // Minimal - just circles, auto-fit will adjust
    } else if (selectedFieldType === FIELD_TYPES.APPROVE || selectedFieldType === FIELD_TYPES.DECLINE) {
      width = 80; height = 30;   // Minimal action button
    } else if (selectedFieldType === FIELD_TYPES.DATE) {
      width = 100; height = 24;  // Tight date input
    } else if (selectedFieldType === FIELD_TYPES.ATTACHMENT) {
      width = 120; height = 30;  // Minimal attachment field
    } else if (selectedFieldType === FIELD_TYPES.NOTE) {
      width = 120; height = 40;  // Minimal note box
    } else if (selectedFieldType === FIELD_TYPES.DROPDOWN) {
      width = 100; height = 24;  // Tight dropdown
    } else {
      // Text, Email, Name, Company, Title - tight input boxes
      width = 100; height = 24;
    }
    
    const newField = {
      id: `field_${Date.now()}`,
      type: selectedFieldType,
      page: pageNumber,
      x: x / scale,
      y: y / scale,
      width,
      height,
      label: `${selectedFieldType} ${fields.length + 1}`,
      required: selectedFieldType !== FIELD_TYPES.NOTE && selectedFieldType !== FIELD_TYPES.ATTACHMENT,
      recipientIndex: 0,
      options: (selectedFieldType === FIELD_TYPES.RADIO || selectedFieldType === FIELD_TYPES.DROPDOWN)
        ? ['Option 1', 'Option 2']
        : [],
      // For radio groups, store individual option positions (relative to group bounds, 0-1)
      radioPositions: selectedFieldType === FIELD_TYPES.RADIO
        ? [
            { x: 0.05, y: 0.15 }, // Option 1 position
            { x: 0.05, y: 0.55 }  // Option 2 position
          ]
        : undefined,
      formula: selectedFieldType === FIELD_TYPES.FORMULA ? '' : undefined,
      placeholder: selectedFieldType === FIELD_TYPES.TEXT || selectedFieldType === FIELD_TYPES.EMAIL
        ? `Enter ${selectedFieldType}...`
        : undefined
    };
    
    setFields(prev => [...prev, newField]);
    setSelectedFieldType(null);
    toast.success('Field added');
  };

  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    setSelectedField(field);
    setIsDragging(true);
    
    const fieldElement = e.currentTarget;
    const rect = fieldElement.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    // Handle radio option dragging within group
    if (draggingRadioOption) {
      setFields(prev => {
        const field = prev.find(f => f.id === draggingRadioOption.fieldId);
        if (!field) return prev;

        const fieldElement = document.querySelector(`[data-field-id="${field.id}"]`);
        if (!fieldElement) return prev;

        const fieldRect = fieldElement.getBoundingClientRect();
        const fieldWidth = field.width * scale;
        const fieldHeight = field.height * scale;

        // Calculate new position as ratio within the group (0-1)
        const relX = (e.clientX - fieldRect.left - radioOptionOffset.x) / fieldWidth;
        const relY = (e.clientY - fieldRect.top - radioOptionOffset.y) / fieldHeight;

        // Clamp to group bounds
        const clampedX = Math.max(0, Math.min(0.85, relX));
        const clampedY = Math.max(0, Math.min(0.85, relY));

        // Update the radio position
        const newPositions = [...(field.radioPositions || [])];
        newPositions[draggingRadioOption.optionIndex] = { x: clampedX, y: clampedY };

        return prev.map(f =>
          f.id === field.id
            ? { ...f, radioPositions: newPositions }
            : f
        );
      });
      return;
    }

    if (!isDragging || !selectedField) return;

    const rect = containerRef.current.getBoundingClientRect();

    // Use pixel-based coordinates (no scale division needed since we use width prop)
    const x = e.clientX - rect.left - dragOffset.x;
    const y = e.clientY - rect.top - dragOffset.y;

    setFields(prev => prev.map(f =>
      f.id === selectedField.id
        ? { ...f, x: Math.max(0, x), y: Math.max(0, y) }
        : f
    ));
  };

  const handleMouseUp = () => {
    // If we were dragging a radio option, auto-fit the container
    if (draggingRadioOption) {
      // Use autoFitRadioGroupById which reads from latest state
      autoFitRadioGroupById(draggingRadioOption.fieldId);
    }
    setIsDragging(false);
    setDraggingRadioOption(null);
  };

  // Auto-fit radio group container to tightly wrap radio buttons (circles only)
  // Labels are rendered outside the bounding box calculation
  const autoFitRadioGroup = (field) => {
    if (!field.radioPositions || field.radioPositions.length === 0) return;

    const RADIO_CIRCLE_SIZE = 16; // Only the circle, not the label
    const PADDING = 4; // Minimal padding around circles

    // Current container position and dimensions
    const containerX = field.x;
    const containerY = field.y;
    const containerWidth = field.width;
    const containerHeight = field.height;

    // Calculate absolute page positions of each radio button circle
    const absolutePagePositions = field.radioPositions.map(pos => ({
      x: containerX + (pos.x * containerWidth),
      y: containerY + (pos.y * containerHeight)
    }));

    // Find bounding box on the page - ONLY around circles, not labels
    const pageMinX = Math.min(...absolutePagePositions.map(p => p.x));
    const pageMinY = Math.min(...absolutePagePositions.map(p => p.y));
    const pageMaxX = Math.max(...absolutePagePositions.map(p => p.x)) + RADIO_CIRCLE_SIZE;
    const pageMaxY = Math.max(...absolutePagePositions.map(p => p.y)) + RADIO_CIRCLE_SIZE;

    // New container bounds with minimal padding - tight fit around circles only
    const newX = Math.max(0, pageMinX - PADDING);
    const newY = Math.max(0, pageMinY - PADDING);
    const newWidth = Math.max(24, (pageMaxX - pageMinX) + (PADDING * 2));
    const newHeight = Math.max(24, (pageMaxY - pageMinY) + (PADDING * 2));

    // Recalculate radio positions as ratios within the new container
    const newRadioPositions = absolutePagePositions.map(pos => ({
      x: Math.max(0.05, Math.min(0.9, (pos.x - newX) / newWidth)),
      y: Math.max(0.05, Math.min(0.9, (pos.y - newY) / newHeight))
    }));

    const updatedField = {
      ...field,
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
      radioPositions: newRadioPositions
    };

    // Update the field with new container bounds and adjusted radio positions
    setFields(prev => prev.map(f => f.id === field.id ? updatedField : f));
    // Also update selectedField to stay in sync
    setSelectedField(updatedField);
  };

  // Handle radio option drag start
  const handleRadioOptionMouseDown = (e, field, optionIndex) => {
    e.stopPropagation();
    setSelectedField(field);
    setDraggingRadioOption({ fieldId: field.id, optionIndex });

    const optionElement = e.currentTarget;
    const rect = optionElement.getBoundingClientRect();
    setRadioOptionOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle resize start for radio group
  // Radio group resize - only SE corner
  const handleRadioResizeStart = (e, field) => {
    e.stopPropagation();
    setIsDragging(false);
    setIsResizing(true);

    const startWidth = field.width;
    const startHeight = field.height;
    const startX = e.clientX;
    const startY = e.clientY;
    const fieldId = field.id;

    const handleResize = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const newWidth = Math.max(24, startWidth + deltaX / scale);
      const newHeight = Math.max(24, startHeight + deltaY / scale);

      setFields(prev => prev.map(f =>
        f.id === fieldId
          ? { ...f, width: newWidth, height: newHeight }
          : f
      ));
    };

    const stopResize = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
    };

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);
  };

  // Multi-directional resize for non-radio fields
  // direction: 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'
  const handleFieldResizeStart = (e, field, direction) => {
    e.stopPropagation();
    setIsDragging(false);
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startFieldX = field.x;
    const startFieldY = field.y;
    const startWidth = field.width;
    const startHeight = field.height;
    const fieldId = field.id;
    const MIN_SIZE = 20;

    const handleResize = (moveEvent) => {
      const deltaX = (moveEvent.clientX - startX) / scale;
      const deltaY = (moveEvent.clientY - startY) / scale;

      let newX = startFieldX;
      let newY = startFieldY;
      let newWidth = startWidth;
      let newHeight = startHeight;

      // Handle horizontal resizing
      if (direction.includes('e')) {
        newWidth = Math.max(MIN_SIZE, startWidth + deltaX);
      }
      if (direction.includes('w')) {
        const widthChange = Math.min(deltaX, startWidth - MIN_SIZE);
        newX = startFieldX + widthChange;
        newWidth = startWidth - widthChange;
      }

      // Handle vertical resizing
      if (direction.includes('s')) {
        newHeight = Math.max(MIN_SIZE, startHeight + deltaY);
      }
      if (direction.includes('n')) {
        const heightChange = Math.min(deltaY, startHeight - MIN_SIZE);
        newY = startFieldY + heightChange;
        newHeight = startHeight - heightChange;
      }

      setFields(prev => prev.map(f =>
        f.id === fieldId
          ? { ...f, x: newX, y: newY, width: newWidth, height: newHeight }
          : f
      ));

      // Keep selectedField in sync
      setSelectedField(prev => prev?.id === fieldId
        ? { ...prev, x: newX, y: newY, width: newWidth, height: newHeight }
        : prev
      );
    };

    const stopResize = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
    };

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);
  };

  const handleDeleteField = (fieldId) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    setSelectedField(null);
    toast.success('Field deleted');
  };

  const handleUpdateField = (fieldId, updates) => {
    setFields(prev => {
      const newFields = prev.map(f => f.id === fieldId ? { ...f, ...updates } : f);
      // Also update selectedField if it's the one being updated
      const updatedField = newFields.find(f => f.id === fieldId);
      if (updatedField) {
        setSelectedField(updatedField);
      }
      return newFields;
    });
  };

  const handleAddOption = (fieldId) => {
    setFields(prev => {
      const field = prev.find(f => f.id === fieldId);
      if (!field || !(field.type === FIELD_TYPES.RADIO || field.type === FIELD_TYPES.CHECKBOX || field.type === FIELD_TYPES.DROPDOWN)) {
        return prev;
      }

      const newOptions = [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`];
      let updates = { options: newOptions };

      if (field.type === FIELD_TYPES.RADIO) {
        const currentPositions = field.radioPositions || [];
        const OPTION_SPACING_PX = 24; // Fixed pixel spacing between options

        // Calculate absolute page positions of existing options
        const absolutePositions = currentPositions.map(pos => ({
          x: field.x + (pos.x * field.width),
          y: field.y + (pos.y * field.height)
        }));

        // New option below the last one (or at container top if no options)
        const lastAbsPos = absolutePositions[absolutePositions.length - 1] ||
          { x: field.x + 8, y: field.y + 8 };
        const newAbsolutePos = {
          x: lastAbsPos.x,
          y: lastAbsPos.y + OPTION_SPACING_PX
        };

        // Add new absolute position
        absolutePositions.push(newAbsolutePos);

        // Expand container to fit all options
        const maxY = Math.max(...absolutePositions.map(p => p.y)) + 20;
        const newHeight = Math.max(field.height, maxY - field.y + 8);

        // Convert all positions back to ratios
        updates.radioPositions = absolutePositions.map(pos => ({
          x: Math.max(0.02, (pos.x - field.x) / field.width),
          y: Math.max(0.05, (pos.y - field.y) / newHeight)
        }));
        updates.height = newHeight;
      }

      const updatedField = { ...field, ...updates };
      const newFields = prev.map(f => f.id === fieldId ? updatedField : f);

      // Update selectedField to stay in sync
      setSelectedField(updatedField);

      // Schedule auto-fit for radio groups after state update
      if (field.type === FIELD_TYPES.RADIO) {
        setTimeout(() => autoFitRadioGroupById(fieldId), 0);
      }

      return newFields;
    });
  };

  const handleUpdateOption = (fieldId, optionIndex, newValue) => {
    setFields(prev => {
      const field = prev.find(f => f.id === fieldId);
      if (!field) return prev;

      const newOptions = [...field.options];
      newOptions[optionIndex] = newValue;
      const updatedField = { ...field, options: newOptions };
      const newFields = prev.map(f => f.id === fieldId ? updatedField : f);

      setSelectedField(updatedField);
      return newFields;
    });
  };

  const handleDeleteOption = (fieldId, optionIndex) => {
    setFields(prev => {
      const field = prev.find(f => f.id === fieldId);
      if (!field || field.options.length <= 1) return prev;

      const newOptions = field.options.filter((_, i) => i !== optionIndex);
      let updates = { options: newOptions };

      if (field.type === FIELD_TYPES.RADIO && field.radioPositions) {
        updates.radioPositions = field.radioPositions.filter((_, i) => i !== optionIndex);
      }

      const updatedField = { ...field, ...updates };
      const newFields = prev.map(f => f.id === fieldId ? updatedField : f);

      // Update selectedField to stay in sync
      setSelectedField(updatedField);

      // Schedule auto-fit for radio groups after state update
      if (field.type === FIELD_TYPES.RADIO) {
        setTimeout(() => autoFitRadioGroupById(fieldId), 0);
      }

      return newFields;
    });
  };

  // Auto-fit by fieldId (uses latest state) - fits only around circles, not labels
  const autoFitRadioGroupById = (fieldId) => {
    setFields(prev => {
      const field = prev.find(f => f.id === fieldId);
      if (!field || !field.radioPositions || field.radioPositions.length === 0) return prev;

      const RADIO_CIRCLE_SIZE = 16; // Only the circle, not the label
      const PADDING = 4; // Minimal padding around circles

      // Current container position and dimensions
      const containerX = field.x;
      const containerY = field.y;
      const containerWidth = field.width;
      const containerHeight = field.height;

      // Calculate absolute page positions of each radio button circle
      const absolutePagePositions = field.radioPositions.map(pos => ({
        x: containerX + (pos.x * containerWidth),
        y: containerY + (pos.y * containerHeight)
      }));

      // Find bounding box on the page - ONLY around circles, not labels
      const pageMinX = Math.min(...absolutePagePositions.map(p => p.x));
      const pageMinY = Math.min(...absolutePagePositions.map(p => p.y));
      const pageMaxX = Math.max(...absolutePagePositions.map(p => p.x)) + RADIO_CIRCLE_SIZE;
      const pageMaxY = Math.max(...absolutePagePositions.map(p => p.y)) + RADIO_CIRCLE_SIZE;

      // New container bounds with minimal padding - tight fit around circles only
      const newX = Math.max(0, pageMinX - PADDING);
      const newY = Math.max(0, pageMinY - PADDING);
      const newWidth = Math.max(24, (pageMaxX - pageMinX) + (PADDING * 2));
      const newHeight = Math.max(24, (pageMaxY - pageMinY) + (PADDING * 2));

      // Recalculate radio positions as ratios within the new container
      const newRadioPositions = absolutePagePositions.map(pos => ({
        x: Math.max(0.05, Math.min(0.9, (pos.x - newX) / newWidth)),
        y: Math.max(0.05, Math.min(0.9, (pos.y - newY) / newHeight))
      }));

      const updatedField = {
        ...field,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
        radioPositions: newRadioPositions
      };

      setSelectedField(updatedField);
      return prev.map(f => f.id === fieldId ? updatedField : f);
    });
  };

  // Recipient management functions for template flow
  const addRecipient = () => {
    // Check if we can add another recipient
    const signerCount = recipients.filter(r => r.role === 'Signer').length;
    // New recipients default to CC if signer already exists
    const newRole = signerCount >= 1 ? 'CC' : 'Signer';
    const newRecipient = {
      name: newRole === 'Signer' ? `Signer ${recipients.length + 1}` : `CC ${recipients.filter(r => r.role === 'CC').length + 1}`,
      email: '',
      role: newRole
    };
    setRecipients([...recipients, newRecipient]);
  };

  const removeRecipient = (index) => {
    if (recipients.length === 1) {
      toast.error('At least one recipient is required');
      return;
    }
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    // If role changes to Signer, update name if it was a CC name
    if (field === 'role' && value === 'Signer' && updated[index].name.startsWith('CC ')) {
      updated[index].name = `Signer ${index + 1}`;
    } else if (field === 'role' && value === 'CC' && updated[index].name.startsWith('Signer ')) {
      updated[index].name = `CC ${recipients.filter(r => r.role === 'CC').length + 1}`;
    }
    setRecipients(updated);
  };

  const handleSaveFields = async () => {
    try {
      setIsSaving(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      
      // Convert field coordinates to ratios (0-1) for compatibility with signing flow
      // Get the ACTUAL rendered PDF dimensions from the container
      const pdfContainer = containerRef.current;
      let pdfRenderWidth = 612;
      let pdfRenderHeight = 792;
      
      if (pdfContainer) {
        // Get the first PDF page canvas to determine actual rendered size
        const pdfPage = pdfContainer.querySelector('.react-pdf__Page__canvas');
        if (pdfPage) {
          pdfRenderWidth = pdfPage.clientWidth;
          pdfRenderHeight = pdfPage.clientHeight;
          console.log(`Using actual PDF render dimensions: ${pdfRenderWidth}x${pdfRenderHeight}`);
        } else {
          console.warn('PDF canvas not found, using default dimensions 612x792');
        }
      }
      
      const PDF_BASE_WIDTH = pdfRenderWidth;
      const PDF_BASE_HEIGHT = pdfRenderHeight;
      
      const fieldsWithRatios = fields.map(field => {
        // Build props object properly for all field types
        // Start with existing props to preserve all properties (like format for date fields)
        const props = {
          ...field.props,  // Preserve existing props (format, maxLength, etc.)
          label: field.label,
          required: field.required,
          placeholder: field.placeholder
        };
        
        // For radio and checkbox-group, ensure options are in props with correct positions
        if (field.type === 'radio' || field.type === 'checkbox-group') {
          // Get options from field, or use defaults if empty/missing
          let options = field.options || [];
          const radioPositions = field.radioPositions || [];

          // If options array is empty, provide defaults
          if (options.length === 0) {
            options = ['Option 1', 'Option 2'];
            console.warn(`Field ${field.id} of type ${field.type} had no options, using defaults`);
          }

          // Use radioPositions array for x,y coordinates (these are the actual user-placed positions)
          props.options = options.map((opt, idx) => {
            // Get position from radioPositions array if available
            const pos = radioPositions[idx];
            const defaultX = 0.1;
            const defaultY = 0.15 + (idx * 0.35);

            // Convert string options to proper format with x, y positions from radioPositions
            if (typeof opt === 'string') {
              return {
                label: opt,
                value: opt.toLowerCase().replace(/\s+/g, '_'),
                x: pos?.x !== undefined ? pos.x : defaultX,
                y: pos?.y !== undefined ? pos.y : defaultY
              };
            }
            // For object options, prefer radioPositions over opt's own x,y
            return {
              ...opt,
              x: pos?.x !== undefined ? pos.x : (opt.x !== undefined && opt.x !== null ? opt.x : defaultX),
              y: pos?.y !== undefined ? pos.y : (opt.y !== undefined && opt.y !== null ? opt.y : defaultY)
            };
          });

          console.log(`Saving radio field ${field.id} with positions:`, props.options.map(o => ({ label: o.label, x: o.x, y: o.y })));
        }
        
        // For formula fields, preserve formula
        if (field.type === 'formula' && field.formula) {
          props.formula = field.formula;
        }

        // For date fields, ensure dateFormat is saved as props.format
        if (field.type === 'date') {
          // field.dateFormat is set only when the user explicitly changes the dropdown.
          // If the user never changes it, field.dateFormat stays undefined even though
          // the dropdown visually shows the default 'YYYY-MM-DD'. So we must always
          // fall back to field.props?.format (previously saved) or the hard default.
          props.format = field.dateFormat || field.props?.format || 'YYYY-MM-DD';
          console.log(`💾 Saving date field ${field.id} with props.format: "${props.format}" (field.dateFormat="${field.dateFormat}", field.props?.format="${field.props?.format}")`);
        }

        return {
          ...field,
          props,
          // Keep x, y, width, height at field level for backwards compatibility
          x: field.x / PDF_BASE_WIDTH,
          y: field.y / PDF_BASE_HEIGHT,
          w: field.width / PDF_BASE_WIDTH,
          h: field.height / PDF_BASE_HEIGHT,
          width: field.width / PDF_BASE_WIDTH,
          height: field.height / PDF_BASE_HEIGHT,
          rect: {
            x: field.x / PDF_BASE_WIDTH,
            y: field.y / PDF_BASE_HEIGHT,
            w: field.width / PDF_BASE_WIDTH,
            h: field.height / PDF_BASE_HEIGHT,
            width: field.width / PDF_BASE_WIDTH,
            height: field.height / PDF_BASE_HEIGHT,
            page: field.page
          }
        };
      });
      
      // Save fields to the latest version's schema
      await axios.put(`${API}/templates/${id}/fields`, { fields: fieldsWithRatios }, { headers });
      
      toast.success('Fields saved successfully!');
    } catch (error) {
      toast.error('Failed to save fields');
    } finally {
      setIsSaving(false);
    }
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case FIELD_TYPES.SIGNATURE: return <PenTool size={16} />;
      case FIELD_TYPES.INITIALS: return <span className="text-xs font-bold">IN</span>;
      case FIELD_TYPES.DATE: return <Calendar size={16} />;
      case FIELD_TYPES.TEXT: return <Type size={16} />;
      case FIELD_TYPES.EMAIL: return <span className="text-xs font-bold">@</span>;
      case FIELD_TYPES.NAME: return <span className="text-xs font-bold">N</span>;
      case FIELD_TYPES.COMPANY: return <span className="text-xs font-bold">C</span>;
      case FIELD_TYPES.TITLE: return <span className="text-xs font-bold">T</span>;
      case FIELD_TYPES.CHECKBOX: return <CheckSquare size={16} />;
      case FIELD_TYPES.RADIO: return <Circle size={16} />;
      case FIELD_TYPES.DROPDOWN: return <span className="text-xs font-bold">▼</span>;
      case FIELD_TYPES.ATTACHMENT: return <span className="text-xs font-bold">📎</span>;
      case FIELD_TYPES.FORMULA: return <span className="text-xs font-bold">fx</span>;
      case FIELD_TYPES.NOTE: return <span className="text-xs font-bold">💬</span>;
      case FIELD_TYPES.APPROVE: return <span className="text-xs font-bold">✓</span>;
      case FIELD_TYPES.DECLINE: return <span className="text-xs font-bold">✗</span>;
      default: return null;
    }
  };

  const getFieldColor = (type) => {
    switch (type) {
      case FIELD_TYPES.SIGNATURE: return 'bg-purple-500';
      case FIELD_TYPES.INITIALS: return 'bg-purple-400';
      case FIELD_TYPES.DATE: return 'bg-pink-500';
      case FIELD_TYPES.TEXT: return 'bg-yellow-500';
      case FIELD_TYPES.EMAIL: return 'bg-orange-500';
      case FIELD_TYPES.NAME: return 'bg-blue-400';
      case FIELD_TYPES.COMPANY: return 'bg-cyan-500';
      case FIELD_TYPES.TITLE: return 'bg-teal-500';
      case FIELD_TYPES.CHECKBOX: return 'bg-green-500';
      case FIELD_TYPES.RADIO: return 'bg-blue-500';
      case FIELD_TYPES.DROPDOWN: return 'bg-indigo-500';
      case FIELD_TYPES.ATTACHMENT: return 'bg-gray-500';
      case FIELD_TYPES.FORMULA: return 'bg-red-500';
      case FIELD_TYPES.NOTE: return 'bg-yellow-400';
      case FIELD_TYPES.APPROVE: return 'bg-green-600';
      case FIELD_TYPES.DECLINE: return 'bg-red-600';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/templates/${id}`)}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 truncate">Field Editor</h1>
              <p className="text-gray-600 text-sm truncate">{template?.name}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              onClick={handleSaveFields}
              disabled={isSaving || fields.length === 0}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
              size="sm"
            >
              <Save size={16} className="mr-2" />
              {isSaving ? 'Saving...' : 'Save Fields'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Toolbar */}
          <div className="col-span-1 md:col-span-2">
            <Card>
              <CardContent className="pt-6 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                <h3 className="font-semibold mb-4">Field Types</h3>
                
                {/* Signature Fields */}
                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">SIGNATURE</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.SIGNATURE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.SIGNATURE)}
                >
                  <PenTool size={14} className="mr-2" />
                  Signature
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.INITIALS ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.INITIALS)}
                >
                  <span className="text-xs font-bold mr-2">IN</span>
                  Initials
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.DATE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.DATE)}
                >
                  <Calendar size={14} className="mr-2" />
                  Date Signed
                </Button>

                {/* Input Fields */}
                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">STANDARD FIELDS</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.NAME ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.NAME)}
                >
                  <span className="text-xs font-bold mr-2">N</span>
                  Name
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.EMAIL ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.EMAIL)}
                >
                  <span className="text-xs font-bold mr-2">@</span>
                  Email
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.COMPANY ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.COMPANY)}
                >
                  <span className="text-xs font-bold mr-2">C</span>
                  Company
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.TITLE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.TITLE)}
                >
                  <span className="text-xs font-bold mr-2">T</span>
                  Title
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.TEXT ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.TEXT)}
                >
                  <Type size={14} className="mr-2" />
                  Text
                </Button>

                {/* Choice Fields */}
                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">SELECTION FIELDS</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.CHECKBOX ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.CHECKBOX)}
                >
                  <CheckSquare size={14} className="mr-2" />
                  Checkbox
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.RADIO ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.RADIO)}
                >
                  <Circle size={14} className="mr-2" />
                  Radio
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.DROPDOWN ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.DROPDOWN)}
                >
                  <span className="text-xs font-bold mr-2">▼</span>
                  Dropdown
                </Button>

                {/* Advanced Fields */}
                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">ADVANCED</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.ATTACHMENT ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.ATTACHMENT)}
                >
                  <span className="text-xs mr-2">📎</span>
                  Attachment
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.FORMULA ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.FORMULA)}
                >
                  <span className="text-xs font-bold mr-2">fx</span>
                  Formula
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.NOTE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.NOTE)}
                >
                  <span className="text-xs mr-2">💬</span>
                  Note
                </Button>

                {/* Action Buttons */}
                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">ACTIONS</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.APPROVE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.APPROVE)}
                >
                  <span className="text-xs font-bold mr-2">✓</span>
                  Approve
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.DECLINE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.DECLINE)}
                >
                  <span className="text-xs font-bold mr-2">✗</span>
                  Decline
                </Button>

                {selectedFieldType && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      <MousePointer size={14} className="inline mr-1" />
                      Click on the document to place the field
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setSelectedFieldType(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Fields: {fields.length}</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {fields.map((field) => (
                      <div
                        key={field.id}
                        className={`text-xs p-2 rounded cursor-pointer ${
                          selectedField?.id === field.id ? 'bg-indigo-100 border border-indigo-300' : 'bg-gray-50'
                        }`}
                        onClick={() => setSelectedField(field)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            {getFieldIcon(field.type)}
                            {field.label}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteField(field.id);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* PDF Viewer */}
          <div className="col-span-1 md:col-span-7">
            <Card>
              <CardContent className="pt-6">
                {pdfFile ? (
                  <div
                    className={`border border-gray-300 rounded ${selectedFieldType ? 'cursor-crosshair' : 'cursor-default'}`}
                    style={{
                      maxHeight: 'calc(100vh - 180px)',
                      overflowY: 'scroll',
                      overflowX: 'auto'
                    }}
                  >
                    <div
                      ref={containerRef}
                      className="relative"
                      style={{ display: 'inline-block', minWidth: '100%' }}
                      onClick={selectedFieldType ? handleAddField : undefined}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                    >
                      <Document
                        file={pdfFile}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => {
                          console.error('❌ PDF load error:', error);
                          toast.error('Failed to load PDF document. Please check console for details.');
                        }}
                        loading={
                          <div className="flex items-center justify-center h-[600px]">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading document...</p>
                            </div>
                          </div>
                        }
                      >
                        <Page
                          pageNumber={pageNumber}
                          width={pageWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>

                      {/* Render fields */}
                    {fields
                      .filter(f => f.page === pageNumber)
                      .map((field) => (
                        <div
                          key={field.id}
                          data-field-id={field.id}
                          className={`absolute ${
                            field.type === FIELD_TYPES.RADIO
                              ? (selectedField?.id === field.id ? 'border border-dashed border-indigo-400' : 'border-0')
                              : `border-2 ${selectedField?.id === field.id ? 'border-indigo-600 shadow-lg' : 'border-gray-400'} ${getFieldColor(field.type)} bg-opacity-40`
                          } cursor-pointer ${field.type === FIELD_TYPES.RADIO ? '' : 'hover:border-indigo-400'}`}
                          style={{
                            left: `${field.x * scale}px`,
                            top: `${field.y * scale}px`,
                            width: `${field.width * scale}px`,
                            height: `${field.height * scale}px`,
                            zIndex: 10,
                            backgroundColor: field.type === FIELD_TYPES.RADIO ? 'transparent' : undefined
                          }}
                          onMouseDown={(e) => handleFieldMouseDown(e, field)}
                        >
                          {/* Render radio group with draggable options */}
                          {field.type === FIELD_TYPES.RADIO && field.options?.length > 0 ? (
                            <div className="relative w-full h-full overflow-visible">
                              {field.options.map((option, idx) => {
                                // Get position from radioPositions or use default
                                const pos = field.radioPositions?.[idx] || { x: 0.05, y: 0.15 + idx * 0.35 };
                                return (
                                  <div
                                    key={idx}
                                    className="absolute flex items-center cursor-move hover:bg-blue-50 rounded p-0.5"
                                    style={{
                                      left: `${pos.x * 100}%`,
                                      top: `${pos.y * 100}%`,
                                      whiteSpace: 'nowrap'
                                    }}
                                    onMouseDown={(e) => handleRadioOptionMouseDown(e, field, idx)}
                                    title="Drag to reposition"
                                  >
                                    <Circle size={12} className="text-blue-500 flex-shrink-0" />
                                  </div>
                                );
                              })}

                              {/* Resize handle for radio group */}
                              {selectedField?.id === field.id && (
                                <div
                                  className="absolute -bottom-1 -right-1 w-3 h-3 bg-indigo-600 border border-white cursor-se-resize rounded-sm"
                                  onMouseDown={(e) => handleRadioResizeStart(e, field)}
                                  title="Drag to resize"
                                />
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Minimal field display - icon only, no label to keep box tight */}
                              <div className="flex items-center justify-center h-full text-xs font-semibold text-gray-700">
                                {getFieldIcon(field.type)}
                              </div>
                              {/* Resize handles on all sides and corners for non-radio fields */}
                              {selectedField?.id === field.id && (
                                <>
                                  {/* Corner handles */}
                                  <div className="absolute -top-1 -left-1 w-2 h-2 bg-indigo-600 border border-white cursor-nw-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'nw')} />
                                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-600 border border-white cursor-ne-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'ne')} />
                                  <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-indigo-600 border border-white cursor-sw-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'sw')} />
                                  <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-indigo-600 border border-white cursor-se-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'se')} />
                                  {/* Edge handles */}
                                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-1.5 bg-indigo-600 border border-white cursor-n-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'n')} />
                                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-1.5 bg-indigo-600 border border-white cursor-s-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 's')} />
                                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-4 bg-indigo-600 border border-white cursor-w-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'w')} />
                                  <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-4 bg-indigo-600 border border-white cursor-e-resize rounded-sm" onMouseDown={(e) => handleFieldResizeStart(e, field, 'e')} />
                                </>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No document uploaded yet</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => navigate(`/templates/${id}`)}
                    >
                      Upload Document
                    </Button>
                  </div>
                )}
                
                {/* Page Controls */}
                {numPages && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                        disabled={pageNumber <= 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm py-2 px-4">
                        Page {pageNumber} of {numPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                        disabled={pageNumber >= numPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Properties */}
          <div className="col-span-1 md:col-span-3 space-y-4">

            {/* Properties Panel */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">Field Properties</h3>
                
                {selectedField ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Label</label>
                      <Input
                        value={selectedField.label}
                        onChange={(e) => handleUpdateField(selectedField.id, { label: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Type</label>
                      <Input value={selectedField.type} disabled />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Assigned Recipient</label>
                      <select
                        value={selectedField.recipientIndex}
                        onChange={(e) => handleUpdateField(selectedField.id, { recipientIndex: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {recipients.map((recipient, idx) => (
                          <option key={idx} value={idx}>
                            {recipient.name || `Recipient ${idx + 1}`} ({recipient.role})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {recipients[selectedField.recipientIndex]?.role === 'CC' && (
                          <span className="text-amber-600">Note: CC recipients cannot sign fields</span>
                        )}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={selectedField.required || false}
                        onChange={(e) => {
                          handleUpdateField(selectedField.id, { required: e.target.checked });
                          toast.success(`Field marked as ${e.target.checked ? 'required' : 'optional'}`);
                        }}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <label className="text-sm cursor-pointer" onClick={() => {
                        const newRequired = !selectedField.required;
                        handleUpdateField(selectedField.id, { required: newRequired });
                        toast.success(`Field marked as ${newRequired ? 'required' : 'optional'}`);
                      }}>
                        Required Field
                      </label>
                    </div>

                    {/* Options for Radio, Checkbox, Dropdown */}
                    {(selectedField.type === FIELD_TYPES.RADIO ||
                      selectedField.type === FIELD_TYPES.CHECKBOX ||
                      selectedField.type === FIELD_TYPES.DROPDOWN) && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">Options</label>
                        <div className="space-y-2">
                          {selectedField.options?.map((option, index) => (
                            <div key={index} className="flex gap-2">
                              <Input
                                value={option}
                                onChange={(e) => handleUpdateOption(selectedField.id, index, e.target.value)}
                                placeholder={`Option ${index + 1}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteOption(selectedField.id, index)}
                                disabled={selectedField.options.length <= 1}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleAddOption(selectedField.id)}
                          >
                            <Plus size={14} className="mr-1" />
                            Add Option
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {selectedField.type === FIELD_TYPES.CHECKBOX
                            ? 'Users can select multiple options'
                            : selectedField.type === FIELD_TYPES.DROPDOWN
                            ? 'Users select from a dropdown list'
                            : 'Users can select only one option. Drag radio buttons to position them.'}
                        </p>

                        {/* Auto-fit button for radio groups */}
                        {selectedField.type === FIELD_TYPES.RADIO && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => autoFitRadioGroup(selectedField)}
                          >
                            Auto-fit Container
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Formula for Formula Fields */}
                    {selectedField.type === FIELD_TYPES.FORMULA && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">Formula</label>
                        <Input
                          value={selectedField.formula || ''}
                          onChange={(e) => handleUpdateField(selectedField.id, { formula: e.target.value })}
                          placeholder="e.g., [field1] + [field2]"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use [fieldName] to reference other fields
                        </p>
                      </div>
                    )}

                    {/* Placeholder for Text/Email Fields */}
                    {(selectedField.type === FIELD_TYPES.TEXT ||
                      selectedField.type === FIELD_TYPES.EMAIL ||
                      selectedField.type === FIELD_TYPES.NAME ||
                      selectedField.type === FIELD_TYPES.COMPANY ||
                      selectedField.type === FIELD_TYPES.TITLE) && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">Placeholder</label>
                        <Input
                          value={selectedField.placeholder || ''}
                          onChange={(e) => handleUpdateField(selectedField.id, { placeholder: e.target.value })}
                          placeholder="Enter placeholder text..."
                        />
                      </div>
                    )}

                    {/* Date Format Selector */}
                    {selectedField.type === FIELD_TYPES.DATE && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">Date Format</label>
                        <select
                          value={selectedField.dateFormat || selectedField.props?.format || 'YYYY-MM-DD'}
                          onChange={(e) => handleUpdateField(selectedField.id, { dateFormat: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        >
                          <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                          <option value="YYYY/MM/DD">YYYY/MM/DD (2024/12/31)</option>
                          <option value="DD-MM-YYYY">DD-MM-YYYY (31-12-2024)</option>
                          <option value="MM-DD-YYYY">MM-DD-YYYY (12-31-2024)</option>
                        </select>
                      </div>
                    )}

                    {/* Max File Size for Attachment */}
                    {selectedField.type === FIELD_TYPES.ATTACHMENT && (
                      <div>
                        <label className="text-sm font-medium mb-2 block">Max File Size (MB)</label>
                        <Input
                          type="number"
                          min="1"
                          max="50"
                          value={selectedField.maxFileSize || 10}
                          onChange={(e) => handleUpdateField(selectedField.id, { maxFileSize: parseInt(e.target.value) })}
                        />
                      </div>
                    )}
                    
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => handleDeleteField(selectedField.id)}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete Field
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Select a field to view and edit its properties
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

    </Layout>
  );
};

export default TemplateFieldEditor;
